import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createPageUrl } from '@/utils';
import { LanguageProvider, useLanguage } from '@/components/ui/LanguageContext';
import { useAuth } from '@/lib/AuthContext';
import { getMyProfileStats } from '@/api/content';
import { MAIN_CAMPUS_ONLY_MESSAGE, isCorrespondenceUser } from '@/components/common/MainCampusOnly';
import { isAdminUser } from '@/lib/adminAccess';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  BookOpen,
  Menu,
  X,
  LogIn,
  ChevronDown,
  Gem,
  Award,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import logoIcon from '@/assets/images/icon-1.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

function Footer() {
  const currentYear = new Date().getFullYear();
  const footerLinks = [
    { label: '運営会社', href: 'https://illustacademia.jp/company/' },
    { label: '利用規約', href: 'https://illustacademia.jp/user-policy/' },
    { label: 'プライバシーポリシー', href: 'https://illustacademia.jp/privacypolicy/' }
  ];

  return (
    <footer className="mt-auto border-t border-[#d4c4b0] bg-[#f5ede3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-3 md:gap-8 text-sm">
          {footerLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#5d4529] hover:text-[#3d2817] transition-colors"
            >
              {item.label}
            </a>
          ))}
          <span className="text-[#6b5540]">© {currentYear} イラストアカデミア</span>
        </div>
      </div>
    </footer>
  );
}

function LayoutContent({ children, currentPageName }) {
  const { t } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const profileDisplayName =
    user?.display_name ||
    user?.global_name ||
    user?.full_name ||
    user?.discord_username ||
    user?.username ||
    'ユーザー';
  const isCorrespondenceStudent = isCorrespondenceUser(user);
  const isAdmin = isAdminUser(user);
  const { data: profileStats } = useQuery({
    queryKey: ['my-profile-stats', user?.id || 'current'],
    queryFn: getMyProfileStats,
    enabled: isAuthenticated && !isCorrespondenceStudent,
  });
  const profileGemCountLabel = profileStats
    ? profileStats.stone.toLocaleString()
    : '...';
  const profileLevelLabel =
    profileStats?.level != null
      ? `${t('profileLevelPrefix')}${profileStats.level}`
      : `${t('profileLevelPrefix')}-`;

  useEffect(() => {
    // no-op: AuthProvider が auth 情報を更新するので、ここで追加チェック不要
  }, [isAuthenticated]);

  const handleLogin = () => {
    navigate('/Login');
  };

  // Pages without layout (Landing page)
  const noLayoutPages = ['Landing'];
  if (noLayoutPages.includes(currentPageName)) {
    return (
      <div className="min-h-screen bg-black">
        {children}
      </div>
    );
  }

  // Legal pages - minimal layout
  const legalPages = ['Terms', 'Privacy', 'Commerce'];
  if (legalPages.includes(currentPageName)) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to={createPageUrl('Home')} className="flex items-center gap-2">
                <img
                  src={logoIcon}
                  alt="イラストアカデミア"
                  className="w-8 h-8 rounded-lg object-cover"
                />
                <span className="font-bold text-xl text-[#f5ede3]">イラストアカデミア</span>
              </Link>
            </div>
          </div>
        </header>
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    );
  }

  const navItems = [
    { name: 'Home', label: 'ホーム', icon: Home },
    { name: 'Catalog', label: '講座', icon: BookOpen },
    ...(isAdmin ? [{ name: 'AdminIndex', label: 'admin専用', icon: ShieldCheck }] : [])
  ];

  const authProfileDropdown = (
    <DropdownMenu>
      <div className="flex items-center gap-1">
        <div className="p-1 rounded-lg">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-[#8b6f47] border border-[#8b6f47]">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={profileDisplayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-medium text-[#f5ede3]">
                {profileDisplayName?.[0] || 'U'}
              </div>
            )}
          </div>
        </div>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1 rounded-lg hover:bg-[#f5ede3] transition-colors"
            aria-label="アカウントメニュー"
          >
            <ChevronDown className="w-4 h-4 text-[#6b5540]" />
          </button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent align="end" className="w-64 border-[#d4c4b0] bg-white">
        <div className="px-3 py-3 border-b border-[#efe5d8] flex gap-3">
          <div className="w-9 h-9 shrink-0 rounded-full overflow-hidden bg-[#8b6f47] border border-[#8b6f47]">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={profileDisplayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-medium text-[#f5ede3]">
                {profileDisplayName?.[0] || 'U'}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[#3d2817] truncate leading-tight">{profileDisplayName}</p>
            {isCorrespondenceStudent ? (
              <p className="mt-2 rounded-lg bg-zinc-100 px-2 py-1 text-xs font-medium text-[#6b5540]">
                {MAIN_CAMPUS_ONLY_MESSAGE}
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                <span className="inline-flex items-center gap-1 text-[#6b5540]">
                  <Gem className="w-3.5 h-3.5 shrink-0 text-amber-600" aria-hidden />
                  <span className="font-semibold tabular-nums text-[#3d2817]">
                    {profileGemCountLabel}
                  </span>
                  <span className="ml-0.5">{t('profileGemsLabel')}</span>
                </span>
                <span className="text-[#d4c4b0]" aria-hidden>
                  ·
                </span>
                <span className="inline-flex items-center gap-1 text-[#6b5540]">
                  <Award className="w-3.5 h-3.5 shrink-0 text-[#8b6f47]" aria-hidden />
                  <span className="font-semibold tabular-nums text-[#3d2817]">
                    {profileLevelLabel}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <DropdownMenuItem
          onClick={() => {
            logout(false);
            navigate('/Home');
          }}
          className="text-red-600 focus:text-red-700 cursor-pointer"
        >
          ログアウト
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] flex flex-col">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50">
        <div className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-11 2xl:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Mobile Header */}
            <div className="md:hidden flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 hover:bg-[#f5ede3] rounded-lg transition-all"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-5 h-5 text-[#5d4529]" />
                  ) : (
                    <Menu className="w-5 h-5 text-[#5d4529]" />
                  )}
                </button>
                <Link to={createPageUrl('Home')}>
                  <img
                    src={logoIcon}
                    alt="イラストアカデミア"
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                </Link>
              </div>
              {isAuthenticated ? (
                authProfileDropdown
              ) : (
                <Button
                  size="sm"
                  onClick={handleLogin}
                  className="bg-[#3d2817] hover:bg-[#2d1810] text-[#f5ede3]"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  ログイン
                </Button>
              )}
            </div>

            {/* Desktop Header */}
            <div className="hidden md:flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Link to={createPageUrl('Home')}>
                  <img
                    src={logoIcon}
                    alt="イラストアカデミア"
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                </Link>
                <nav className="flex items-center gap-1">
                  {navItems.map(item => (
                    <Link
                      key={item.name}
                      to={createPageUrl(item.name)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                        currentPageName === item.name
                          ? 'bg-[#8b6f47] text-[#f5ede3]'
                          : 'text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3]'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </div>

              {isAuthenticated ? (
                authProfileDropdown
              ) : (
                <Button
                  size="sm"
                  onClick={handleLogin}
                  className="bg-[#3d2817] hover:bg-[#2d1810] text-[#f5ede3]"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  {t('discordLogin')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t-2 border-[#8b6f47] bg-[#faf6f1]"
            >
              <nav className="px-4 py-4 space-y-1">
                {navItems.map(item => (
                  <Link
                    key={item.name}
                    to={createPageUrl(item.name)}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      currentPageName === item.name
                        ? 'bg-[#8b6f47] text-[#f5ede3]'
                        : 'text-[#5d4529] hover:text-[#3d2817] hover:bg-[#f5ede3]'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}

              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      <div className="pb-20 md:pb-0">
        <Footer />
      </div>

    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <LanguageProvider>
      <LayoutContent currentPageName={currentPageName}>
        {children}
      </LayoutContent>
    </LanguageProvider>
  );
}